import * as React from 'react';
import { Action } from 'redux';
import { deregisterListener, registerListener } from './store/middleware/pubsub';

/**
 * A hook to listen out for actions being dispatched to the store to indicate an error in a Thunk action
 * @param clearActionType The action that indicates the error is cleared (typically `*_REQUEST`, as this means a new run of the action)
 * @param errorActionType The action that indicates an error has occurred (typically `*_FAILURE`)
 *
 * @returns `errored`, a boolean to indicate if the latest invocation of the action has errored
 */
export const useActionErrorNotifier = (clearActionType: string | string[], errorActionType: string) => {
	const [errored, setErrored] = React.useState(false);

	React.useEffect(() => {
		if (Array.isArray(clearActionType)) {
			// Map each action listener key to its action
			const actionMapping = {};

			clearActionType.forEach((clearAction) => {
				const key = Math.random().toString(36);
				registerListener(key, clearAction, () => setErrored(false));
				actionMapping[key] = clearAction;
			});

			return () => {
				Object.entries(actionMapping).forEach(([key, act]) => deregisterListener(key, act));
			};
		} else {
			const key = Math.random().toString(36);
			registerListener(key, clearActionType, () => {
				setErrored(false);
			});
			return () => deregisterListener(key, clearActionType);
		}
	}, []);

	React.useEffect(() => {
		const key = Math.random().toString(36);
		registerListener(key, errorActionType, () => {
			setErrored(true);
		});
		return () => deregisterListener(key, errorActionType);
	}, []);

	return errored;
};

export type ReduxListener = (action: Action) => any;
/**
 * A hook that can listen for one or more redux actions being dispatched into the store, and perform
 * a callback when they are.
 * @param callback The callback to perform. This will be called with the invoked action.
 * @param actions The action or actions to listen for.
 */
export const useActionCallback = (callback: ReduxListener, ...actions: string[]) => {
	React.useEffect(() => {
		// Map each action listener key to its action
		const actionMapping = {};

		actions.forEach((a) => {
			const key = Math.random().toString(36);
			registerListener(key, a, callback);
			actionMapping[key] = a;
		});

		return () => {
			Object.entries(actionMapping).forEach(([key, act]) => deregisterListener(key, act));
		};
	}, []);
};
